// ---------------------------------------------------------------
// Vehicle photo storage in the browser (IndexedDB).
// Photos are resized before saving so they stay small.
// Note: photos live in this browser; when the dashboard moves to the
// server we can swap this file for server uploads without UI changes.
// ---------------------------------------------------------------
// Use the single shared connection (defined in loadStore) so photos, drivers,
// loads and trailers all share one connection and version.
import { openDb } from './loadStore';

const STORE = 'vehicle-photos';
const STORE_FILES = 'driver-files';

async function filesGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_FILES, 'readonly').objectStore(STORE_FILES).get(key);
    tx.onsuccess = () => resolve(tx.result || null);
    tx.onerror = () => reject(tx.error);
  });
}

async function filesSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_FILES, 'readwrite').objectStore(STORE_FILES).put(value, key);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ---- Driver photo + documents ----
export const getDriverPhoto = (driverId) => filesGet(`driver:${driverId}:photo`);
export const saveDriverPhoto = (driverId, dataUrl) => filesSet(`driver:${driverId}:photo`, dataUrl);
export const getDriverDocs = async (driverId) => (await filesGet(`driver:${driverId}:docs`)) || [];
export const saveDriverDocs = (driverId, docs) => filesSet(`driver:${driverId}:docs`, docs);

// ---- Safety / DQ documents (one file per checklist item) ----
export const getSafetyDoc = (driverId, itemKey) => filesGet(`driver:${driverId}:safety:${itemKey}`);
export const saveSafetyDoc = (driverId, itemKey, dataUrl) => filesSet(`driver:${driverId}:safety:${itemKey}`, dataUrl);

// ---- Rental agreement files (kind = 'license' | 'signature') ----
export const getRentalFile = (rentalId, kind) => filesGet(`rental:${rentalId}:${kind}`);
export const saveRentalFile = (rentalId, kind, dataUrl) => filesSet(`rental:${rentalId}:${kind}`, dataUrl);

// ---- Per-rental vehicle-condition photos (own set per rental) ----
export const saveRentalPhoto = (rentalId, slot, dataUrl) => filesSet(`rental:${rentalId}:photo:${slot}`, dataUrl);
export async function getRentalPhotos(rentalId) {
  const out = {};
  await Promise.all(ALL_SLOTS.map(async (s) => { out[s] = await filesGet(`rental:${rentalId}:photo:${s}`); }));
  return out;
}

// Any file (PDF, image…) to a data URL, unchanged
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('could not read file'));
    r.readAsDataURL(file);
  });
}

async function dbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    tx.onsuccess = () => resolve(tx.result || null);
    tx.onerror = () => reject(tx.error);
  });
}

async function dbSet(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite').objectStore(STORE).put(value, key);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite').objectStore(STORE).delete(key);
    tx.onsuccess = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Photo slots: front, left, right, back, inside0..inside3
export const SIDE_SLOTS = ['front', 'left', 'right', 'back'];
export const INSIDE_SLOTS = ['inside0', 'inside1', 'inside2', 'inside3'];
export const ALL_SLOTS = [...SIDE_SLOTS, ...INSIDE_SLOTS];

const key = (deviceId, slot) => `${deviceId}:${slot}`;

export async function getVehiclePhotos(deviceId) {
  const out = {};
  await Promise.all(ALL_SLOTS.map(async (s) => { out[s] = await dbGet(key(deviceId, s)); }));
  return out;
}

export const savePhoto = (deviceId, slot, dataUrl) => dbSet(key(deviceId, slot), dataUrl);
export const removePhoto = (deviceId, slot) => dbDelete(key(deviceId, slot));

const isHeicFile = (file) => /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');

// iPhone/Mac HEIC photos: convert to JPEG in the browser first. Some HEIC
// variants (Live Photos / newer HEIF) can't be decoded by the bundled libheif;
// if conversion fails we hand back the original and let the browser try (Safari
// can decode HEIC natively), falling to a friendly message otherwise.
async function normalizeFile(file) {
  if (!isHeicFile(file)) return file;
  try {
    const { default: heic2any } = await import('heic2any');
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
    return Array.isArray(blob) ? blob[0] : blob;
  } catch {
    return file; // fall through; fileToResizedDataUrl will surface a clear message
  }
}

// Resize any image file down to max 1000px and compress to JPEG
export async function fileToResizedDataUrl(rawFile, maxSize = 1000) {
  const wasHeic = isHeicFile(rawFile);
  const file = await normalizeFile(rawFile);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(wasHeic
        ? 'This iPhone HEIC photo couldn’t be converted here. Use the "Take photo" button instead, or on your iPhone set Settings → Camera → Formats → "Most Compatible", or upload a JPEG/screenshot.'
        : 'unsupported image format'));
    };
    img.src = url;
  });
}
